import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useTranslations from '@/i18n/useTranslations';
import UserService from '@/services/user.service';

import {
  INK_DRIFT_STRENGTH,
  MAX_STROKES,
  SAVE_LIMIT,
} from './constants';
import { getLocalAngle, randomAngle, TAU } from './geometry';
import { renderBloomCanvas, renderWedgeCanvas } from './rendering';

const MAX_IMAGE_BYTES = 950_000;
const DOWNSCALE_FACTOR = 0.85;
const MAX_DOWNSCALE_ATTEMPTS = 8;

function stripPngPrefix(dataUrl) {
  return dataUrl.replace(/^data:image\/png;base64,/, '');
}

function decodedByteLength(base64) {
  const clean = base64.replace(/\s+/g, '');
  return atob(clean).length;
}

function createScaledCanvas(sourceCanvas, scale) {
  if (scale >= 1) {
    return sourceCanvas;
  }

  const width = Math.max(1, Math.floor(sourceCanvas.width * scale));
  const height = Math.max(1, Math.floor(sourceCanvas.height * scale));

  const scaledCanvas = document.createElement('canvas');
  scaledCanvas.width = width;
  scaledCanvas.height = height;

  const ctx = scaledCanvas.getContext('2d');
  ctx.drawImage(sourceCanvas, 0, 0, width, height);

  return scaledCanvas;
}

function exportCanvasAsPngUnderLimit(canvas, t) {
  if (!canvas) {
    throw new Error(t('errors.reveal_first'));
  }

  let scale = 1;
  let candidateCanvas = canvas;
  let previousWidth = canvas.width;
  let previousHeight = canvas.height;

  for (let attempt = 0; attempt < MAX_DOWNSCALE_ATTEMPTS; attempt += 1) {
    const dataUrl = candidateCanvas.toDataURL('image/png');
    const base64 = stripPngPrefix(dataUrl);
    const exactBytes = decodedByteLength(base64);

    if (exactBytes < MAX_IMAGE_BYTES) {
      return { dataUrl };
    }

    scale *= DOWNSCALE_FACTOR;
    const nextWidth = Math.max(1, Math.floor(canvas.width * scale));
    const nextHeight = Math.max(1, Math.floor(canvas.height * scale));

    if (nextWidth === previousWidth && nextHeight === previousHeight) {
      break;
    }

    candidateCanvas = createScaledCanvas(canvas, scale);
    previousWidth = nextWidth;
    previousHeight = nextHeight;
  }

  throw new Error(t('errors.image_too_large'));
}

function normaliseKaleidoscopePattern(rawPattern, fallback = {}) {
  const pattern = rawPattern ?? {};
  const base = fallback ?? {};
  const inlineImageData = pattern.imageData ?? pattern.image_data ?? null;

  const url =
    pattern.url ??
    pattern.imageUrl ??
    pattern.image_url ??
    pattern.imageDataUrl ??
    pattern.image_data_url ??
    pattern.image ??
    (inlineImageData
      ? inlineImageData.startsWith('data:image/png;base64,')
        ? inlineImageData
        : `data:image/png;base64,${inlineImageData}`
      : null) ??
    base.url;

  if (!url) {
    return null;
  }

  const timestamp =
    pattern.timestamp ??
    pattern.savedAt ??
    pattern.saved_at ??
    pattern.createdAt ??
    pattern.created_at ??
    base.timestamp ??
    new Date().toISOString();

  const folds =
    pattern.folds ??
    pattern.foldCount ??
    pattern.fold_count ??
    base.folds ??
    null;

  const organicMode =
    pattern.organicMode ??
    pattern.organic_mode ??
    base.organicMode ??
    false;

  const smoothingEnabled =
    pattern.smoothingEnabled ??
    pattern.smoothing_enabled ??
    base.smoothingEnabled ??
    true;

  const inkDriftEnabled =
    pattern.inkDriftEnabled ??
    pattern.ink_drift_enabled ??
    base.inkDriftEnabled ??
    false;

  const strokeCount =
    pattern.strokeCount ??
    pattern.strokes ??
    pattern.stroke_count ??
    base.strokeCount ??
    null;

  const id = pattern.id ?? pattern._id ?? base.id ?? timestamp ?? Date.now();

  return {
    id,
    url,
    timestamp,
    folds,
    organicMode: Boolean(organicMode),
    smoothingEnabled: Boolean(smoothingEnabled),
    inkDriftEnabled: Boolean(inkDriftEnabled),
    strokeCount: typeof strokeCount === 'number' ? strokeCount : null,
  };
}

export const useKaleidoscopeEngine = ({ onUpgradeRequired } = {}) => {
  const t = useTranslations('common.newspaper.art_games.kaleidoscope.game');
  const containerRef = useRef(null);
  const wedgeCanvasRef = useRef(null);
  const bloomCanvasRef = useRef(null);
  const wedgeCtxRef = useRef(null);
  const bloomCtxRef = useRef(null);
  const geometryRef = useRef(null);
  const strokesRef = useRef([]);
  const currentStrokeRef = useRef(null);
  const [folds, setFolds] = useState(8);
  const [revealStartAngle, setRevealStartAngle] = useState(() => randomAngle());
  const [isDrawing, setIsDrawing] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [hasStroke, setHasStroke] = useState(false);
  const [bloomKey, setBloomKey] = useState(0);
  const [organicMode, setOrganicMode] = useState(false);
  const [smoothingEnabled, setSmoothingEnabled] = useState(true);
  const [inkDriftEnabled, setInkDriftEnabled] = useState(false);
  const [activeSwitch, setActiveSwitch] = useState('smoothing');
  const [canvasSize, setCanvasSize] = useState(520);
  const [strokesVersion, setStrokesVersion] = useState(0);
  const [savedPatterns, setSavedPatterns] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [saveToast, setSaveToast] = useState(null);
  const saveToastTimeoutRef = useRef(null);

  const showSaveToast = useCallback((message) => {
    clearTimeout(saveToastTimeoutRef.current);
    setSaveToast(message);

    if (message) {
      saveToastTimeoutRef.current = setTimeout(() => {
        setSaveToast(null);
      }, 2500);
    }
  }, []);

  const strokeBudgetSlots = useMemo(() => Array.from({ length: MAX_STROKES }, (_, index) => index), []);

  const wedgeAngle = useMemo(() => TAU / folds, [folds]);
  const displayStartAngle = useMemo(() => -Math.PI / 2 - wedgeAngle / 2, [wedgeAngle]);

  const geometry = useMemo(
    () => ({
      startAngle: displayStartAngle,
      wedgeAngle,
      radius: canvasSize * 0.49,
      centerX: canvasSize / 2,
      centerY: canvasSize / 2,
    }),
    [displayStartAngle, wedgeAngle, canvasSize],
  );

  useEffect(() => {
    geometryRef.current = geometry;
  }, [geometry]);

  const clampValue = useCallback((value, min, max) => Math.min(max, Math.max(min, value)), []);

  const getCanvasCoordinates = useCallback((event, canvas) => {
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return null;
    }

    return {
      x: ((event.clientX - rect.left) / rect.width) * canvasSize,
      y: ((event.clientY - rect.top) / rect.height) * canvasSize,
    };
  }, [canvasSize]);

  const stylisePoint = useCallback(
    (x, y) => {
      let nextX = x;
      let nextY = y;
      const geometry = geometryRef.current;

      if (inkDriftEnabled && geometry) {
        const driftRange = geometry.radius * INK_DRIFT_STRENGTH;
        if (driftRange > 0) {
          const driftedX = x + (Math.random() - 0.5) * driftRange * 2;
          const driftedY = y + (Math.random() - 0.5) * driftRange * 2;
          const dx = driftedX - geometry.centerX;
          const dy = driftedY - geometry.centerY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const angle = Math.atan2(dy, dx);
          const localAngle = getLocalAngle(angle, geometry.startAngle);
          if (distance <= geometry.radius && localAngle <= geometry.wedgeAngle) {
            nextX = driftedX;
            nextY = driftedY;
          }
        }
      }

      const point = { x: nextX, y: nextY, rawX: x, rawY: y };

      return point;
    },
    [clampValue, inkDriftEnabled],
  );

  const applyExclusiveSwitch = useCallback(
    (nextActive) => {
      setActiveSwitch(nextActive);
      setSmoothingEnabled(nextActive === 'smoothing');
      setOrganicMode(nextActive === 'organic');
      setInkDriftEnabled(nextActive === 'ink-drift');
    },
    [setInkDriftEnabled, setOrganicMode, setSmoothingEnabled],
  );

  const handleExclusiveSwitch = useCallback(
    (switchId, enabled) => {
      if (enabled) {
        applyExclusiveSwitch(switchId);
      } else if (activeSwitch === switchId) {
        applyExclusiveSwitch(null);
      }
    },
    [activeSwitch, applyExclusiveSwitch],
  );
  const resizeCanvas = useCallback(() => {
    if (!containerRef.current) return;
    const { offsetWidth } = containerRef.current;
    const styles = window.getComputedStyle(containerRef.current);
    const paddingX = parseFloat(styles.paddingLeft || '0') + parseFloat(styles.paddingRight || '0');
    const availableWidth = offsetWidth - paddingX;
    if (availableWidth <= 0) return;
    const size = Math.min(760, availableWidth);
    setCanvasSize(size);
  }, []);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas]);

  useEffect(
    () => () => {
      clearTimeout(saveToastTimeoutRef.current);
    },
    [],
  );

  const renderWedge = useCallback(() => {
    const ctx = wedgeCtxRef.current;
    if (!ctx) return;

    renderWedgeCanvas({
      ctx,
      canvasSize,
      geometry: geometryRef.current,
      strokes: strokesRef.current,
      currentStroke: currentStrokeRef.current,
      smoothingEnabled,
    });
  }, [canvasSize, smoothingEnabled]);

  useEffect(() => {
    renderWedge();
  }, [displayStartAngle, folds, renderWedge, smoothingEnabled, strokesVersion]);

  const renderBloom = useCallback(() => {
    const ctx = bloomCtxRef.current;
    if (!ctx) return;

    renderBloomCanvas({
      ctx,
      canvasSize,
      geometry: geometryRef.current,
      folds,
      strokes: strokesRef.current,
      smoothingEnabled,
      organicMode,
      revealStartAngle,
    });
  }, [canvasSize, folds, organicMode, revealStartAngle, smoothingEnabled]);

  useEffect(() => {
    const wedgeCanvas = wedgeCanvasRef.current;
    const bloomCanvas = bloomCanvasRef.current;
    if (!wedgeCanvas || !bloomCanvas) return;

    const dpr = window.devicePixelRatio || 1;

    const setupCanvas = (canvas) => {
      canvas.width = canvasSize * dpr;
      canvas.height = canvasSize * dpr;
      canvas.style.width = `${canvasSize}px`;
      canvas.style.height = `${canvasSize}px`;
      const context = canvas.getContext('2d');
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      return context;
    };

    wedgeCtxRef.current = setupCanvas(wedgeCanvas);
    bloomCtxRef.current = setupCanvas(bloomCanvas);

    renderWedge();
    if (isRevealed) {
      renderBloom();
    }
  }, [canvasSize, bloomKey, isRevealed, renderBloom, renderWedge]);

  const handlePointerDown = useCallback(
    (event) => {
      if (isRevealed) return;
      if (strokesRef.current.length >= MAX_STROKES) return;

      const canvas = wedgeCanvasRef.current;
      if (!canvas) return;

      canvas.setPointerCapture?.(event.pointerId);

      const coordinates = getCanvasCoordinates(event, canvas);
      if (!coordinates) return;
      const { x, y } = coordinates;
      const geo = geometryRef.current;
      const dx = x - geo.centerX;
      const dy = y - geo.centerY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx);
      const localAngle = getLocalAngle(angle, geo.startAngle);

      if (distance > geo.radius || localAngle > geo.wedgeAngle) {
        return;
      }

      currentStrokeRef.current = {
        points: [stylisePoint(x, y)],
      };

      setIsDrawing(true);
      setHasStroke(true);
      renderWedge();
    },
    [getCanvasCoordinates, isRevealed, renderWedge, stylisePoint],
  );

  const handlePointerMove = useCallback(
    (event) => {
      if (!isDrawing || !currentStrokeRef.current) return;

      const canvas = wedgeCanvasRef.current;
      if (!canvas) return;

      const coordinates = getCanvasCoordinates(event, canvas);
      if (!coordinates) return;
      const { x, y } = coordinates;
      const geo = geometryRef.current;

      const dx = x - geo.centerX;
      const dy = y - geo.centerY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx);
      const localAngle = getLocalAngle(angle, geo.startAngle);

      if (distance > geo.radius || localAngle > geo.wedgeAngle) {
        return;
      }

      const points = currentStrokeRef.current.points;
      const lastPoint = points[points.length - 1];
      if (lastPoint) {
        const delta = Math.hypot((lastPoint.rawX ?? lastPoint.x) - x, (lastPoint.rawY ?? lastPoint.y) - y);
        if (delta < 1.2) {
          return;
        }
      }

      points.push(stylisePoint(x, y));
      renderWedge();
    },
    [getCanvasCoordinates, isDrawing, renderWedge, stylisePoint],
  );

  const handlePointerUp = useCallback(
    (event) => {
      if (!isDrawing || !currentStrokeRef.current) return;

      const canvas = wedgeCanvasRef.current;
      canvas?.releasePointerCapture?.(event.pointerId);

      const stroke = currentStrokeRef.current;
      currentStrokeRef.current = null;
      setIsDrawing(false);

      if (stroke.points.length >= 1) {
        strokesRef.current = [...strokesRef.current, stroke];
        setStrokesVersion((prev) => prev + 1);
        renderWedge();
        return;
      }

      setHasStroke(strokesRef.current.length > 0);
      renderWedge();
    },
    [isDrawing, renderWedge],
  );

  useEffect(() => {
    const canvas = wedgeCanvasRef.current;
    if (!canvas) return undefined;

    const handleLeave = () => {
      if (!isDrawing) return;
      currentStrokeRef.current = null;
      setIsDrawing(false);
    };

    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('pointerup', handlePointerUp);
    canvas.addEventListener('pointercancel', handleLeave);
    canvas.addEventListener('pointerleave', handleLeave);

    return () => {
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerup', handlePointerUp);
      canvas.removeEventListener('pointercancel', handleLeave);
      canvas.removeEventListener('pointerleave', handleLeave);
    };
  }, [handlePointerDown, handlePointerMove, handlePointerUp, isDrawing]);

  const resetCanvas = useCallback(() => {
    strokesRef.current = [];
    currentStrokeRef.current = null;
    setIsDrawing(false);
    setIsRevealed(false);
    setHasStroke(false);
    setRevealStartAngle(randomAngle());
    setStrokesVersion(0);
    renderWedge();
  }, [renderWedge]);

  const revealPattern = useCallback(() => {
    if (!hasStroke || strokesRef.current.length === 0) return;
    setIsRevealed(true);
    setBloomKey(Date.now());
    renderBloom();
  }, [hasStroke, renderBloom]);

  const remixPattern = useCallback(
    (nextFolds) => {
      setFolds(nextFolds);
      setBloomKey(Date.now());
      setTimeout(() => {
        renderBloom();
      }, 0);
    },
    [renderBloom],
  );

  useEffect(() => {
    if (!isRevealed) return;
    renderBloom();
  }, [folds, organicMode, renderBloom, isRevealed, smoothingEnabled, strokesVersion]);

  useEffect(() => {
    let isMounted = true;

    const loadPatterns = async () => {
      try {
        const { response, data } = await UserService.listKaleidoscopePatterns();
        if (!response?.ok) {
          throw new Error(data?.message ?? 'Unable to fetch your gallery.');
        }

        const payloadData = data?.data ?? data ?? {};
        const list = Array.isArray(payloadData?.patterns)
          ? payloadData.patterns
          : Array.isArray(payloadData)
            ? payloadData
            : [];

        const parsed = list
          .map((item) => normaliseKaleidoscopePattern(item))
          .filter(Boolean)
          .slice(0, SAVE_LIMIT);

        if (isMounted) {
          setSavedPatterns(parsed);
        }
      } catch (error) {
        console.error('Failed to load kaleidoscope patterns', error);
      }
    };

    loadPatterns();

    return () => {
      isMounted = false;
    };
  }, []);

  const savePattern = useCallback(async () => {
    if (isSaving) return;
    if (!isRevealed) {
      showSaveToast(t('errors.reveal_before_saving'));
      return;
    }

    const canvas = bloomCanvasRef.current;
    if (!canvas) return;

    let exportResult;

    try {
      exportResult = exportCanvasAsPngUnderLimit(canvas, t);
    } catch (error) {
      console.error('Failed to export kaleidoscope pattern', error);
      showSaveToast(error?.message ?? t('errors.default_save'));
      return;
    }

    const { dataUrl } = exportResult;
    const savedAt = new Date();
    const timestamp = savedAt.toISOString();
    const payload = {
      imageData: dataUrl,
      timestamp,
      savedAt: timestamp,
      folds,
      organicMode,
      smoothingEnabled,
      inkDriftEnabled,
      strokeCount: strokesRef.current.length,
      revealStartAngle,
    };

    setIsSaving(true);
    try {
      const { response, data } = await UserService.saveKaleidoscopePattern(payload);

      if (!response?.ok) {
        if (data?.code === 'SUARTE_PLUS_REQUIRED' || data?.data?.code === 'SUARTE_PLUS_REQUIRED') {
          onUpgradeRequired?.(data);
          return;
        }
        const message = data?.message ?? t('errors.default_save');
        throw new Error(message);
      }

      const payloadData = data?.data ?? data ?? {};
      const savedPattern = normaliseKaleidoscopePattern(
        payloadData?.pattern ?? payloadData,
        {
          id: savedAt.getTime(),
          url: dataUrl,
          timestamp: payload.timestamp,
          folds: payload.folds,
          organicMode: payload.organicMode,
          smoothingEnabled: payload.smoothingEnabled,
          strokeCount: payload.strokeCount,
        },
      );

      const hydratedPatterns = Array.isArray(payloadData?.patterns) ? payloadData.patterns : null;

      if (hydratedPatterns) {
        setSavedPatterns(
          hydratedPatterns
            .map((item) => normaliseKaleidoscopePattern(item))
            .filter(Boolean)
            .slice(0, SAVE_LIMIT),
        );
      } else if (savedPattern) {
        setSavedPatterns((prev) => {
          const next = [savedPattern, ...prev];
          return next.slice(0, SAVE_LIMIT);
        });
      }

      showSaveToast(t('toasts.saved'));
    } catch (error) {
      console.error('Failed to save kaleidoscope pattern', error);
      showSaveToast(error?.message ?? t('errors.default_save'));
    } finally {
      setIsSaving(false);
    }
  }, [
    folds,
    inkDriftEnabled,
    isRevealed,
    isSaving,
    onUpgradeRequired,
    organicMode,
    revealStartAngle,
    showSaveToast,
    smoothingEnabled,
    t,
  ]);

  const deletePattern = useCallback(async (patternId) => {
    if (!patternId || isDeleting) return false;

    setIsDeleting(true);
    try {
      const { response, data } = await UserService.deleteKaleidoscopePattern(patternId);
      if (!response?.ok) {
        throw new Error(data?.message ?? 'Unable to delete this saved pattern.');
      }

      setSavedPatterns((patterns) => patterns.filter((pattern) => pattern.id !== patternId));
      return true;
    } catch (error) {
      console.error('Failed to delete kaleidoscope pattern', error);
      showSaveToast(error?.message ?? 'Unable to delete this saved pattern.');
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [isDeleting, showSaveToast]);

  const usedBudget = useMemo(
    () => strokesRef.current.length + (currentStrokeRef.current ? 1 : 0),
    [strokesVersion, isDrawing],
  );

  const availableForDrawing = useMemo(
    () => !isRevealed && strokesRef.current.length < MAX_STROKES,
    [isRevealed, strokesVersion],
  );

  const bloomClassName = `kaleidoscope-bloom ${isRevealed ? 'is-visible' : ''}`;

  const undoStroke = useCallback(() => {
    if (isRevealed) return;
    if (strokesRef.current.length === 0) return;
    strokesRef.current = strokesRef.current.slice(0, -1);
    setHasStroke(strokesRef.current.length > 0);
    setStrokesVersion((prev) => prev + 1);
    renderWedge();
  }, [isRevealed, renderWedge]);

  const canUndo = useMemo(() => !isRevealed && strokesRef.current.length > 0, [isRevealed, strokesVersion]);

  const handleSmoothingToggle = useCallback(
    (enabled) => handleExclusiveSwitch('smoothing', enabled),
    [handleExclusiveSwitch],
  );

  const handleOrganicToggle = useCallback(
    (enabled) => handleExclusiveSwitch('organic', enabled),
    [handleExclusiveSwitch],
  );

  const handleInkDriftToggle = useCallback(
    (enabled) => handleExclusiveSwitch('ink-drift', enabled),
    [handleExclusiveSwitch],
  );

  return {
    availableForDrawing,
    bloomCanvasRef,
    bloomClassName,
    bloomKey,
    canUndo,
    containerRef,
    displayStartAngle,
    deletePattern,
    folds,
    hasStroke,
    isRevealed,
    isDeleting,
    isSaving,
    inkDriftEnabled,
    organicMode,
    remixPattern,
    resetCanvas,
    revealPattern,
    revealStartAngle,
    saveToast,
    savedPatterns,
    savePattern,
    setFolds,
    setInkDriftEnabled: handleInkDriftToggle,
    setOrganicMode: handleOrganicToggle,
    setSmoothingEnabled: handleSmoothingToggle,
    smoothingEnabled,
    strokeBudgetSlots,
    undoStroke,
    usedBudget,
    wedgeCanvasRef,
  };
};
